import {
	Button,
	ButtonGroup,
	Content,
	DropZone,
	Heading,
	IllustratedMessage,
	ProgressCircle,
	Text,
} from '@react-spectrum/s2';
import CloudUpload from '@react-spectrum/s2/illustrations/gradient/generic1/CloudUpload';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DropEvent } from 'react-aria';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import {
	fileUploadService,
	IMAGE_FILE_INPUT_ACCEPT,
	isAcceptedImageDropType,
} from '@/api/fileUploadApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { BlobImage } from '@/components/blob/BlobImage';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary';
import { logger } from '@/logger.ts';

import type {
	ReferenceImageItem,
	ReferenceImageRequest as ReferenceImageRequestMessage,
} from '../../../../../bridge/types/messages.ts';
import { splitFilesToCapacity } from '../promptAttachments.ts';

import { MessageRow } from './MessageRow.tsx';

type SlotState = {
	preview: string | null;
	url: string | null;
	isUploading: boolean;
	error: string | null;
};

const initialSlot = (image_url: string | null | undefined): SlotState => ({
	preview: null,
	url: image_url ?? null,
	isUploading: false,
	error: null,
});

const cleanLabel = (label: string): string =>
	label.replace(/\s*\(optional\)\s*$/i, '').trim();

type SubmitState = 'idle' | 'sending' | 'timedOut' | 'failed';

// Success is not the write alone. It is the write, plus the delta broadcast
// back to this browser, plus a re-render that carries the images. Nothing ever
// reports a failure: the bridge has no frame type that carries an error to the
// browser, so a write that was dropped looks exactly like a write still in
// flight. This timer is therefore the only detector the card has, and the cost
// is real — after a failed submit the card says nothing until it fires.
//
// The value sits well above every delay the send path states for itself: the
// preflight check on an idle socket, the per-user compare-and-set retries and
// their backoff, the bridge's re-apply attempts, the broadcast, and the
// re-render. No measurement of the whole round trip exists, so pick a value
// far above those bounds rather than fit one to a number nobody has.
const SUBMIT_TIMEOUT_MS = 60_000;

// A photo was saved only if the same slots came back, so compare content. A
// reconnect replaces the whole user_state object, and an identity check would
// then read any reconnect as a successful submit.
const sameImages = (
	sent: ReferenceImageItem[] | null,
	stored: ReferenceImageItem[] | undefined,
): boolean =>
	sent !== null &&
	stored !== undefined &&
	sent.length === stored.length &&
	sent.every(
		(item, i) =>
			item.label === stored[i].label && item.image_url === stored[i].image_url,
	);

// The card states only what it knows. It never says "check your connection",
// because the write can fail while the connection is healthy, and that
// sentence sends the customer to fix something that is not broken.
const submitMessageFor = (
	submitState: SubmitState,
	isConnected: boolean,
): string | null => {
	if (submitState === 'sending') {
		return isConnected
			? null
			: 'Waiting for your connection. Your photos will send when you are back online.';
	}
	if (submitState === 'timedOut') {
		return 'We have not had confirmation that your photos were saved. Press Submit to try again.';
	}
	if (submitState === 'failed') {
		return 'We could not confirm that your photos were saved. Send a message in the chat and we will pick this up there.';
	}
	return null;
};

export const ReferenceImageRequest = ({
	message,
	chatId,
}: {
	message: ReferenceImageRequestMessage;
	chatId: string;
}) => {
	const bridgeApi = useBridgeApi();
	const userId = useUserId();
	const addImageToLibrary = useAddImageToLibrary(userId);

	const [slots, setSlots] = useState<SlotState[]>(() =>
		message.items.map((item) => initialSlot(item.image_url)),
	);
	const [notice, setNotice] = useState<string | null>(null);
	const [submitState, setSubmitState] = useState<SubmitState>('idle');
	const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const submitAttemptsRef = useRef(0);
	const sentImagesRef = useRef<ReferenceImageItem[] | null>(null);
	// `pending_reference_images` is chat-scoped and nothing clears it, so an
	// earlier card in this chat can already hold exactly what this press is
	// about to send. That stale match is not confirmation of THIS submit, and
	// reading it as one reports a lost write as saved — the silence this card
	// exists to break. When the store already matches at the press, only the
	// card freeze can confirm.
	const storedMatchedAtSubmitRef = useRef(false);

	const isFrozen = message.submitted === true;
	const isWaitingOnBridge = submitState === 'sending';
	const isConnected = bridgeApi.connectionStatus === 'connected';

	// The bridge writes the submitted photos here on both of its branches, even
	// the one where it cannot find the message to freeze. Reading the freeze
	// alone would leave the card waiting on a submit that was in fact saved.
	const storedImages =
		bridgeApi.user_state?.chats?.[chatId]?.pending_reference_images;
	const isSubmitStored =
		!storedMatchedAtSubmitRef.current &&
		sameImages(sentImagesRef.current, storedImages);

	useEffect(() => {
		if (isFrozen || isSubmitStored) {
			if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
			submitTimerRef.current = null;
			submitAttemptsRef.current = 0;
			// Forget what was sent, so the next press is judged on its own delta
			// and not on the one this submit already matched.
			sentImagesRef.current = null;
			storedMatchedAtSubmitRef.current = false;
			setSubmitState('idle');
		}
	}, [isFrozen, isSubmitStored]);

	// Run the clock only while the browser can actually deliver the action. A
	// submit made offline sits in the pending queue, so counting against it
	// would call a queued photo a failure. When the connection returns the
	// action leaves at that moment, so a fresh full budget starts here.
	useEffect(() => {
		if (submitState !== 'sending' || !isConnected) return;
		submitTimerRef.current = setTimeout(() => {
			submitTimerRef.current = null;
			// One retry, then stop. Without the bound a customer can press through
			// an outage and queue a copy of the action on every press, and each
			// copy starts its own agent turn when the connection returns.
			setSubmitState(submitAttemptsRef.current >= 2 ? 'failed' : 'timedOut');
		}, SUBMIT_TIMEOUT_MS);
		return () => {
			if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
			submitTimerRef.current = null;
		};
	}, [submitState, isConnected]);

	useEffect(
		() => () => {
			if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
		},
		[],
	);

	const setSlot = useCallback((index: number, patch: Partial<SlotState>) => {
		setSlots((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], ...patch };
			return next;
		});
	}, []);

	// A slot accepts a new gesture while its upload still runs, and an upload
	// cannot be cancelled. Each gesture claims the slot and takes the new
	// generation. An upload writes to the slot only while it still holds the
	// current generation, so a slow upload never overwrites a newer photo and
	// never refills a slot the user cleared.
	const slotGenerations = useRef<number[]>([]);
	const claimSlot = useCallback((index: number) => {
		const generation = (slotGenerations.current[index] ?? 0) + 1;
		slotGenerations.current[index] = generation;
		return generation;
	}, []);

	// `slots` state only reflects a claim once React has re-rendered, which
	// happens after the current gesture's handler returns. Two gestures
	// resolved close enough together (e.g. two drops) can both run before that
	// render, so both would see the same slot as empty. This ref is mutated
	// synchronously the moment a slot is claimed and cleared once its upload
	// settles, closing that window regardless of render timing.
	const claimedSlots = useRef<Set<number>>(new Set());

	const uploadToSlot = useCallback(
		async (index: number, file: File) => {
			if (!userId) {
				claimedSlots.current.delete(index);
				return;
			}
			const generation = claimSlot(index);
			const holdsSlot = () => slotGenerations.current[index] === generation;
			setSlot(index, { isUploading: true, error: null });
			try {
				const { previewUrl, error: previewError } =
					await fileUploadService.createImagePreview(file);
				if (!holdsSlot()) return;
				if (previewError || !previewUrl) {
					setSlot(index, {
						isUploading: false,
						error: previewError ?? 'invalid image',
					});
					return;
				}
				setSlot(index, { preview: previewUrl });
				const result = await fileUploadService.upload(file, userId);
				if (holdsSlot()) {
					setSlot(index, { url: result.url, isUploading: false });
				}
				// The photo reached storage, so the library keeps it even when the
				// user has since replaced or cleared this slot.
				addImageToLibrary(result.url, 'reference');
			} catch (error) {
				logger.error('Reference image upload failed', error);
				if (!holdsSlot()) return;
				setSlot(index, {
					isUploading: false,
					error: error instanceof Error ? error.message : 'upload failed',
				});
			} finally {
				claimedSlots.current.delete(index);
			}
		},
		[userId, setSlot, addImageToLibrary, claimSlot],
	);

	// One gesture can carry several photos, but a slot still holds exactly one.
	// The slot the user picked or dropped on always takes the first photo, even
	// when it already holds one — replacing it is what the gesture asked for.
	// The rest spill into the empty slots, left to right. The card never grows
	// or loses a slot: the server decides how many there are.
	const uploadBatch = useCallback(
		(startIndex: number, files: File[]) => {
			if (files.length === 0) return;
			setNotice(null);
			const targets = [
				startIndex,
				...slots.flatMap((slot, index) =>
					index !== startIndex &&
					slot.url === null &&
					slot.preview === null &&
					!slot.isUploading &&
					!claimedSlots.current.has(index)
						? [index]
						: [],
				),
			];
			const { accepted, overflow } = splitFilesToCapacity(
				files,
				targets.length,
			);
			if (overflow > 0) {
				// `targets` is the room THIS GESTURE had: the clicked slot, which a
				// gesture may replace, plus the genuinely empty ones. That is the
				// number that rejected the photos, so it is the number to report.
				//
				// It is not the count of FREE slots, and calling it that was wrong
				// on a prefilled card: clicking a filled slot on a full card
				// reported "1 free slot" when none was free. Say what the number
				// means instead of renaming the number.
				//
				// The sentence is PAST tense for the same reason. An overflow fills
				// every one of these targets on the next line. The card's remaining
				// room is therefore zero when the notice renders. "can take N more
				// photos" offered room that no longer exists.
				const room = targets.length;
				setNotice(
					`${overflow} ${overflow === 1 ? 'photo' : 'photos'} did not fit. ` +
						`This card had room for ${room} ${room === 1 ? 'photo' : 'photos'}.`,
				);
			}
			// Each upload runs on its own, so one failure only marks its own slot.
			// The target is added to `claimedSlots` before starting its upload, so
			// a batch queued right after (even before this one re-renders) sees it
			// as taken.
			accepted.forEach((file, i) => {
				claimedSlots.current.add(targets[i]);
				void uploadToSlot(targets[i], file);
			});
		},
		[slots, uploadToSlot],
	);

	const handleDrop = useCallback(
		(index: number) => async (e: DropEvent) => {
			const files: File[] = [];
			for (const item of e.items) {
				if (item.kind !== 'file') continue;
				files.push(await item.getFile());
			}
			uploadBatch(index, files);
		},
		[uploadBatch],
	);

	const handleClear = useCallback(
		(index: number) => {
			setNotice(null);
			claimSlot(index);
			setSlot(index, {
				preview: null,
				url: null,
				error: null,
				isUploading: false,
			});
		},
		[setSlot, claimSlot],
	);

	const submit = useCallback(
		(skipAll: boolean) => {
			const images: ReferenceImageItem[] = message.items.map((item, i) => ({
				...item,
				image_url: skipAll ? null : (slots[i]?.url ?? null),
			}));
			submitAttemptsRef.current += 1;
			storedMatchedAtSubmitRef.current = sameImages(images, storedImages);
			sentImagesRef.current = images;
			if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
			submitTimerRef.current = null;
			setNotice(null);
			// Set before the action leaves, so the buttons are already disabled
			// when the customer can press again. One press queues one action,
			// whatever the connection is doing.
			setSubmitState('sending');
			bridgeApi.sendAction({
				type: 'report_reference_images_submitted',
				chat_id: chatId,
				request_id: message.request_id,
				images,
			});
		},
		[bridgeApi, chatId, message.items, message.request_id, slots, storedImages],
	);

	const anyUploading = slots.some((s) => s.isUploading);
	// Refuse the press the card is already serving, and the press it can no
	// longer serve.
	const isSubmitBlocked = submitState === 'sending' || submitState === 'failed';
	const submitMessage = submitMessageFor(submitState, isConnected);
	// Once the card freezes, message.items holds what was actually submitted
	// (image_url is null for every slot the user skipped). The frozen label and
	// images read from it rather than the local slots, because "Skip all" tells
	// the backend "no image" without clearing the slots — a prefilled slot still
	// holds its library photo URL, so reading slots would falsely show it as kept.
	const anySubmitted = message.items.some((i) => i.image_url);
	// Reads the immutable message items, not the live slots: this is a
	// card-origin note ("these slots arrived prefilled from your library"), so
	// it stays stable even if the user clears a prefilled slot before submitting.
	const anySourced = message.items.some((i) => i.source && i.image_url);

	return (
		<MessageRow justify="start">
			<div
				data-testid="reference-image-request"
				className="border-beacon-stroke w-full max-w-[640px] rounded-lg border
					bg-white p-4"
			>
				<div className="flex items-start justify-between gap-3">
					<div>
						<Text
							styles={style({
								font: 'body',
								fontWeight: 'bold',
								fontSize: 'body',
							})}
						>
							References
						</Text>
						<div className="mt-1">
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'gray-700',
								})}
							>
								{isFrozen
									? anySubmitted
										? 'Submitted.'
										: 'Skipped.'
									: anySourced
										? 'Using your saved photos.'
										: 'All optional.'}
							</Text>
						</div>
					</div>
					<div className={isFrozen ? 'invisible' : ''} aria-hidden={isFrozen}>
						<ButtonGroup>
							<Button
								variant="secondary"
								onPress={() => submit(true)}
								isDisabled={anyUploading || isFrozen || isSubmitBlocked}
							>
								Skip all
							</Button>
							<Button
								variant="accent"
								onPress={() => submit(false)}
								isDisabled={anyUploading || isFrozen || isSubmitBlocked}
							>
								{isWaitingOnBridge ? 'Sending…' : 'Submit'}
							</Button>
						</ButtonGroup>
					</div>
				</div>

				<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
					{message.items.map((item, index) => {
						const slot = slots[index];
						const showImage = slot.preview ?? slot.url ?? null;
						const isFilled = !!showImage;
						// After freezing, the image comes from what was actually
						// submitted, not the live slot: "Skip all" leaves a prefilled
						// slot's library URL in place, so rendering the slot would show
						// a photo the user chose to skip.
						const submittedImage = item.image_url;
						return (
							<div
								key={`${message.id}-${index}`}
								className="flex flex-col gap-2"
							>
								<div
									className="aspect-square w-full overflow-hidden rounded-md
										bg-gray-100"
								>
									{isFrozen ? (
										submittedImage ? (
											<BlobImage
												src={submittedImage}
												alt={item.label}
												className="h-full w-full object-cover"
											/>
										) : (
											<div
												className="flex h-full w-full items-center
													justify-center"
											>
												<Text
													styles={style({
														font: 'body',
														fontSize: 'body-sm',
														color: 'gray-700',
													})}
												>
													Skipped
												</Text>
											</div>
										)
									) : (
										<DropZone
											onDrop={handleDrop(index)}
											getDropOperation={(types) =>
												isAcceptedImageDropType(types) ? 'copy' : 'cancel'
											}
											isFilled={isFilled}
											replaceMessage="Drop file to replace"
											styles={style({ height: 'full', maxHeight: 'full' })}
										>
											<div
												className="flex h-full w-full cursor-pointer flex-col
													items-center justify-center"
												onClick={() => fileInputRefs.current[index]?.click()}
											>
												{slot.isUploading ? (
													<ProgressCircle
														aria-label="Uploading reference image"
														isIndeterminate
													/>
												) : showImage ? (
													slot.preview ? (
														<img
															src={slot.preview}
															alt={item.label}
															className="h-full w-full object-cover"
														/>
													) : (
														<BlobImage
															src={slot.url}
															alt={item.label}
															className="h-full w-full object-cover"
														/>
													)
												) : (
													<IllustratedMessage>
														<CloudUpload />
														<Heading>{cleanLabel(item.label)}</Heading>
														{item.hint && <Content>{item.hint}</Content>}
													</IllustratedMessage>
												)}
											</div>
											<input
												ref={(el) => {
													fileInputRefs.current[index] = el;
												}}
												type="file"
												accept={IMAGE_FILE_INPUT_ACCEPT}
												multiple
												className="hidden"
												aria-label={`Upload ${item.label}`}
												onChange={(e) => {
													uploadBatch(index, Array.from(e.target.files ?? []));
													e.target.value = '';
												}}
											/>
										</DropZone>
									)}
								</div>
								<div className="flex min-h-8 items-center justify-between gap-2">
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											fontWeight: 'medium',
										})}
									>
										{cleanLabel(item.label)}
									</Text>
									<div className={isFrozen || !isFilled ? 'invisible' : ''}>
										<Button
											variant="secondary"
											onPress={() => handleClear(index)}
										>
											Clear
										</Button>
									</div>
								</div>
								{slot.error && (
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											color: 'negative',
										})}
									>
										{slot.error}
									</Text>
								)}
							</div>
						);
					})}
				</div>
				{/* The notice describes an upload the customer can still act on.
				    A frozen card has nothing to act on, so a stale line there
				    reads like an unresolved error under finished work.

				    The overflow notice and the submit message come from two
				    unrelated events, so they render side by side. Routing the
				    submit message through `setNotice` would let either one
				    erase the other. */}
				{(notice || submitMessage) && !isFrozen && (
					<div className="mt-3 flex flex-col gap-1">
						{notice && (
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'negative',
								})}
							>
								{notice}
							</Text>
						)}
						{submitMessage && (
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'negative',
								})}
							>
								{submitMessage}
							</Text>
						)}
					</div>
				)}
			</div>
		</MessageRow>
	);
};
